import { useState, useEffect, useCallback, useRef } from 'react'
import { Loader2, MessageSquarePlus, FolderPlus, X, Archive } from 'lucide-react'
import { listFeedback, updateStatus, type FeedbackItem, type ListFeedbackParams } from '../../lib/feedbackApi'
import { FeedbackCard } from './FeedbackCard'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import { useUser } from '../../hooks/useUser'
import type { FeedbackFilterState } from './FeedbackFilters'

interface FeedbackListProps {
  filters: FeedbackFilterState
  createdItem?: FeedbackItem | null
  mergeResult?: {
    mergedItem: FeedbackItem
    sourceIds: number[]
    nonce: number
  } | null
  showArchive?: boolean
  onSelect: (id: number) => void
  onNewFeedback: () => void
  onGroupSelected?: (ids: number[]) => void
  onRefresh?: () => void
  onVoteChange?: (id: number, hasVoted: boolean, voteCount: number) => void
}

function matchesListFilters(
  item: FeedbackItem,
  filters: FeedbackFilterState,
  showArchive: boolean | undefined
): boolean {
  const isArchived = item.status === 'dismissed' || item.status === 'archived'
  if (showArchive) return isArchived
  if (isArchived) return false
  if (filters.type && item.type !== filters.type) return false
  if (filters.status && item.status !== filters.status) return false
  // Newly created items are authored by the current user, so `mine` already matches.
  if (filters.search) {
    const haystack = `${item.title} ${item.description}`.toLowerCase()
    if (!haystack.includes(filters.search.toLowerCase())) return false
  }
  return true
}

export function FeedbackList({
  filters,
  createdItem,
  mergeResult,
  showArchive,
  onSelect,
  onNewFeedback,
  onGroupSelected,
  onRefresh,
  onVoteChange,
}: FeedbackListProps) {
  const user = useUser()
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const perPage = 20

  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const pageRef = useRef(page)
  pageRef.current = page

  const showArchiveRef = useRef(showArchive)
  showArchiveRef.current = showArchive

  const fetchData = useCallback(async () => {
    const f = filtersRef.current
    const params: ListFeedbackParams = {
      page: pageRef.current,
      per_page: perPage,
      sort: f.sort,
    }
    if (f.type) params.type = f.type
    if (f.mine) params.mine = true
    if (f.search) params.search = f.search
    // In archive mode, fetch only archived/dismissed items
    if (showArchiveRef.current) {
      params.include_archived = true
      // Don't pass any status filter — we'll filter client-side for both
    } else {
      if (f.status) params.status = f.status
    }

    try {
      const res = await listFeedback(params)
      // In archive mode, client-filter to only show dismissed/archived
      const filtered = showArchiveRef.current
        ? res.items.filter((i) => i.status === 'dismissed' || i.status === 'archived')
        : res.items
      setItems(filtered)
      setTotal(showArchiveRef.current ? filtered.length : res.total)
    } catch (err) {
      setLoading(false)
      throw err // re-throw for useAutoRefresh backoff
    }
    setLoading(false)
  }, [])

  // Refetch when filters or page change
  useEffect(() => {
    setLoading(true)
    fetchData().catch(() => setLoading(false))
  }, [filters, page, fetchData])

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [filters.type, filters.status, filters.mine, filters.search, filters.sort])

  // Clear selection when data changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [filters, page])

  useEffect(() => {
    if (!createdItem || filters.sort !== 'newest') return
    if (!matchesListFilters(createdItem, filters, showArchive)) return
    if (items.some((item) => item.id === createdItem.id)) return

    setPage(1)
    setItems((prev) => [createdItem, ...prev.filter((item) => item.id !== createdItem.id)].slice(0, perPage))
    setTotal((prev) => prev + 1)
  }, [createdItem, filters, items, showArchive])

  useEffect(() => {
    if (!mergeResult) return

    const sourceSet = new Set(mergeResult.sourceIds)
    let removedVisibleCount = 0

    setItems((prev) => {
      removedVisibleCount = prev.filter((item) => sourceSet.has(item.id)).length
      return prev
        .filter((item) => !sourceSet.has(item.id))
        .map((item) => (item.id === mergeResult.mergedItem.id ? mergeResult.mergedItem : item))
    })

    if (removedVisibleCount > 0) {
      setTotal((prev) => Math.max(0, prev - removedVisibleCount))
    }
  }, [mergeResult])

  useAutoRefresh(fetchData, {
    baseInterval: 30000,
    maxInterval: 120000,
  })

  function handleVoteChange(id: number, hasVoted: boolean, voteCount: number) {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, has_voted: hasVoted, vote_count: voteCount } : item
      )
    )
    onVoteChange?.(id, hasVoted, voteCount)
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  async function handleArchive(id: number) {
    try {
      await updateStatus(id, 'archived')
      setItems((prev) => prev.filter((item) => item.id !== id))
      setTotal((t) => t - 1)
      onRefresh?.()
    } catch {
      // Archive failed
    }
  }

  async function handleBulkUnarchive() {
    const ids = Array.from(selectedIds)
    try {
      for (const id of ids) {
        const item = items.find((i) => i.id === id)
        await updateStatus(id, item?.previous_status || 'backlog')
      }
      setItems((prev) => prev.filter((item) => !selectedIds.has(item.id)))
      setTotal((t) => t - ids.length)
      setSelectedIds(new Set())
      onRefresh?.()
    } catch {
      // Bulk unarchive failed
    }
  }

  async function handleBulkArchive() {
    const ids = Array.from(selectedIds)
    try {
      await Promise.all(ids.map((id) => updateStatus(id, 'archived')))
      setItems((prev) => prev.filter((item) => !selectedIds.has(item.id)))
      setTotal((t) => t - ids.length)
      setSelectedIds(new Set())
      onRefresh?.()
    } catch {
      // Bulk archive failed
    }
  }

  function toggleSelectAll() {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)))
    }
  }

  const totalPages = Math.ceil(total / perPage)

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <MessageSquarePlus className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold mb-1">No feedback yet</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Be the first to share your thoughts!
        </p>
        <button
          onClick={onNewFeedback}
          className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
        >
          Submit Feedback
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Select all (admin only) */}
      {user.isAdmin && onGroupSelected && (
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selectedIds.size === items.length && items.length > 0}
              onChange={toggleSelectAll}
              className="rounded border-border"
            />
            Select all on this page
          </label>
        </div>
      )}

      {items.map((item) => (
        <div key={item.id} className="flex items-start gap-2">
          {/* Checkbox (admin only) */}
          {user.isAdmin && onGroupSelected && (
            <div className="pt-4 shrink-0">
              <input
                type="checkbox"
                checked={selectedIds.has(item.id)}
                onChange={() => toggleSelect(item.id)}
                className="rounded border-border"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <FeedbackCard
              item={item}
              onSelect={onSelect}
              onVoteChange={handleVoteChange}
              onArchive={user.isAdmin ? handleArchive : undefined}
              onAddToTheme={user.isAdmin && onGroupSelected ? (id) => onGroupSelected([id]) : undefined}
              isAdmin={user.isAdmin}
            />
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Floating action bar when items are selected */}
      {selectedIds.size > 0 && user.isAdmin && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-xl shadow-2xl">
          <span className="text-sm font-medium">
            {selectedIds.size} selected
          </span>
          {onGroupSelected && (
            <button
              onClick={() => onGroupSelected(Array.from(selectedIds))}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <FolderPlus className="w-4 h-4" />
              Group into Theme
            </button>
          )}
          {showArchiveRef.current ? (
            <button
              onClick={handleBulkUnarchive}
              className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
            >
              <Archive className="w-4 h-4" />
              Unarchive
            </button>
          ) : (
            <button
              onClick={handleBulkArchive}
              className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
